import { useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { UserIdentity, UserRole } from './types';
import { RoleSelector } from './RoleSelector';

interface UserMenuProps {
  identity: UserIdentity;
  activeRole: UserRole;
  setActiveRole: (role: UserRole) => void;
}

const useStyles = makeStyles(theme => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '4px 8px',
    borderRadius: 8,
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.04)',
    },
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 13,
    fontWeight: 600,
    backgroundColor: '#1d9e75',
  },
  avatarLarge: {
    width: 40,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    backgroundColor: '#1d9e75',
  },
  textBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  },
  name: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  roleLabel: {
    fontSize: 11,
    opacity: 0.45,
    lineHeight: 1.2,
  },
  menuPaper: {
    minWidth: 230,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  },
  header: {
    padding: '16px 16px 12px',
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  email: {
    fontSize: 12,
    opacity: 0.5,
  },
  menuItem: {
    padding: '8px 16px',
  },
  signOut: {
    color: theme.palette.error.main,
  },
}));

export function UserMenu({
  identity,
  activeRole,
  setActiveRole,
}: UserMenuProps) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <ButtonBase
        className={classes.trigger}
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <Avatar className={classes.avatar}>{identity.initials}</Avatar>
        <Box className={classes.textBlock}>
          <Typography className={classes.name}>
            {identity.displayName}
          </Typography>
          <Typography className={classes.roleLabel}>
            {activeRole.name}
          </Typography>
        </Box>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <Box className={classes.header}>
          <Box className={classes.headerTop}>
            <Avatar className={classes.avatarLarge}>
              {identity.initials}
            </Avatar>
            <Box className={classes.headerInfo}>
              <Typography variant="subtitle2">
                {identity.displayName}
              </Typography>
              <Typography className={classes.email}>
                {identity.email}
              </Typography>
            </Box>
          </Box>
          <RoleSelector
            roles={identity.roles}
            activeRole={activeRole}
            onRoleChange={setActiveRole}
          />
        </Box>
        <Divider />
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            setAnchorEl(null);
            navigate('/settings');
          }}
        >
          View profile
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            setAnchorEl(null);
            navigate('/notifications');
          }}
        >
          Notifications
        </MenuItem>
        <Divider />
        <MenuItem
          className={`${classes.menuItem} ${classes.signOut}`}
          onClick={() => setAnchorEl(null)}
        >
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
